import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { useAuthStore } from '../../store/authStore';
import type { EventoAcesso } from '../../types/models';

/** Limites do dia civil LOCAL → ISO (evita janela UTC que desloca o dia no BR). */
function localDayBoundsIso(ymd: string) {
  const [y, m, d] = ymd.split('-').map(Number);
  const start = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
  const end = new Date(y, (m || 1) - 1, d || 1, 23, 59, 59, 999);
  return {
    dataInicio: start.toISOString(),
    dataFim: end.toISOString(),
  };
}

/**
 * Hook para Eventos de Acesso (portaria).
 * GET /escolas/:escolaId/eventos-acesso
 */
export function useEventosAcesso(filters?: {
  data?: string;
  tipo?: string;
  turmaId?: string;
  /** false = sem poll; omitido = 30s (legado). Preferir false com WS conectado. */
  refetchIntervalMs?: number | false;
  /** false enquanto o relógio simulado ainda não sincronizou. */
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const escolaId = useAuthStore((s) => s.escolaId);
  const basePath = `/escolas/${escolaId}/eventos-acesso`;
  const refetchInterval =
    filters?.refetchIntervalMs !== undefined
      ? filters.refetchIntervalMs
      : 30_000;

  const query = useQuery({
    queryKey: ['eventos-acesso', escolaId, filters?.data, filters?.tipo, filters?.turmaId],
    queryFn: async () => {
      const params: Record<string, string> = { limite: '200' };
      if (filters?.data) {
        const bounds = localDayBoundsIso(filters.data);
        params.dataInicio = bounds.dataInicio;
        params.dataFim = bounds.dataFim;
      }
      if (filters?.tipo) params.tipoEvento = filters.tipo;
      if (filters?.turmaId) params.turmaId = filters.turmaId;
      const { data } = await api.get(basePath, { params });
      return (Array.isArray(data) ? data : data.items ?? data.data ?? []) as EventoAcesso[];
    },
    enabled: !!escolaId && (filters?.enabled ?? true),
    refetchInterval,
  });

  const registrar = useMutation({
    /** Alinha-se ao `CriarEventoAcessoDto` (ex.: `origemEvento`, `observacoes`). */
    mutationFn: (payload: object) => api.post(basePath, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['eventos-acesso'] }),
  });

  return {
    eventos: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    registrar,
  };
}
